import { filter, set } from 'lodash';
import { sentenceCase } from 'change-case';
import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// material
import {
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import axios from 'axios';
import { Title } from '@mui/icons-material';
// components
import Page from '../components/Page';
import Label from '../components/Label';
import Scrollbar from '../components/Scrollbar';
import Iconify from '../components/Iconify';
import SearchNotFound from '../components/SearchNotFound';

import { PosterListToolbar, PosterListHead, PosterMoreMenu } from '../sections/reports';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'title', label: 'Title', alignRight: false },
  { id: 'images', label: 'Images', alignRight: false },
  { id: 'action', label: 'Action', alignRight: false },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query, roleFilter) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  let filteredArray = stabilizedThis.map((el) => el[0]);

  // Apply text query filter
  if (query) {
    filteredArray = filteredArray.filter((poster) => {
      if (poster.title.toLowerCase().includes(query.toLowerCase())) {
        return true;
      }
      if (poster.images.toLowerCase().includes(query.toLowerCase())) {
        return true;
      }
      return false; 
    });
  }


  return filteredArray;
}

export default function Poster() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('email'); // Default sort by email
  const [filterName, setFilterName] = useState('');
  const [roleFilter, setRoleFilter] = useState(''); // State for role filter
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // State for poster
  const [poster, setPoster] = useState([]);
  const [editingposterId, setEditingPosterId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [title, setTitle] = useState('');
  const [images, setImages] = useState('');

  const [selectedposter, setSelectedPoster] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  // Fetch poster from the server on component mount
  useEffect(() => {
    fetchposter();
  }, []);

  // Fetch poster from the server
  const fetchposter = async () => {
    try {
      const response = await axios.get('https://cpccbe.org/api/posters/getAllPosters');

      setPoster(response.data);
    } catch (error) {
      console.error('Error fetching poster:', error);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = poster.map((n) => n.email); // Use email as unique identifier
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, email) => {
    const selectedIndex = selected.indexOf(email);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, email);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const handleOpenModal = (posterId) => {
    if (posterId) {
      const postertoEdit = poster.find((poster) => poster._id === posterId);
      if (postertoEdit) {
        setTitle(postertoEdit.title);
        setImages(postertoEdit.images);

        setEditingPosterId(posterId);
      }
    } else {
      setTitle('');
      setImages('');

      setEditingPosterId(null);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);

    setTitle('');
    setImages('');

    setEditingPosterId(null);
  };

  // Handle adding a new poster
  const handleAddposter = async () => {
    try {
      const response = await axios.post('https://cpccbe.org/api/posters/createPoster', { title, images }); // Adjust based on your actual data structure

      fetchposter(); // Refresh poster list after adding
      handleCloseModal(); // Close modal after adding
    } catch (error) {
      console.error('Error adding poster:', error);
    }
  };

  // Handle updating a poster
  const handleUpdateposter = async () => {
    try {
      await axios.put(`https://cpccbe.org/api/posters/updatePoster/${editingposterId}`, { title, images }); // Adjust endpoint as necessary
      fetchposter(); // Refresh poster  list after updating

      handleCloseModal(); // Close modal after updating
    } catch (error) {
      console.error('Error updating poster:', error);
    }
  };

  // Handle deleting poster
  const handleDeleteposter = async (posterId) => {
    if (window.confirm('Are you sure you want to delete this poster?')) {
      try {
        await axios.delete(`https://cpccbe.org/api/posters/deleteposter/${posterId}`); // Adjust endpoint as necessary

        fetchposter(); // Refresh poster list after deletion
      } catch (error) {
        console.error('Error deleting poster:', error);
      }
    }
  };

  const handleViewposter = (poster) => {
    // Open a modal or set state to display poster details
    setSelectedPoster(poster); // Assuming you have state to hold selected consultancy data

    setViewModalOpen(true); // Open the modal for viewing poster details
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - poster.length) : 0;

  // Apply sorting and filtering
  const filteredposter = applySortFilter(poster, getComparator(order, orderBy), filterName, roleFilter);

  const isposterNotFound = filteredposter.length === 0;

  return (
    <Page title="poster">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Poster
          </Typography>
          <Button variant="contained" onClick={handleOpenModal} startIcon={<Iconify icon="eva:plus-fill" />}>
            New Poster
          </Button>
        </Stack>

        <Card>
          <PosterListToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            roleFilter={roleFilter}
            onRoleFilterChange={(e) => setRoleFilter(e.target.value)}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <PosterListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={poster.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {filteredposter.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const { _id, email, role } = row; // Adjust based on your actual data structure
                    const isItemSelected = selected.indexOf(email) !== -1;

                    return (
                      <TableRow
                        hover
                        key={_id}
                        tabIndex={-1}
                        role="checkbox"
                        selected={isItemSelected}
                        aria-checked={isItemSelected}
                      >
                        <TableCell align="left">
                          <IconButton onClick={() => handleViewposter(row)} aria-label="view poster">
                            <Iconify icon="eva:eye-fill" />
                          </IconButton>
                        </TableCell>
                        <TableCell align="left">{row.title}</TableCell>
                        <TableCell align="left">{row.images}</TableCell>

                        <TableCell align="left">
                          <PosterMoreMenu
                            onEdit={() => handleOpenModal(row._id)}
                            onDelete={() => handleDeleteposter(row._id)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>

                {isposterNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={poster.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>

        {/* Modal for Editing poster */}
        <Dialog open={openModal} onClose={handleCloseModal}>
          <DialogTitle>{editingposterId ? 'Edit poster ' : 'Add New poster'}</DialogTitle>
          <DialogContent>
            <FormControl fullWidth margin='dense'>
              <InputLabel id="title">Title</InputLabel>
              <Select
                labelId="title"
                id="title"
                value={title}
                label="Title"
                onChange={(e) => setTitle(e.target.value)}
              >
                <MenuItem value="5S Posters">5"S" Posters</MenuItem>
                <MenuItem value="Safety Posters">Safety Posters</MenuItem>
                <MenuItem value="Quality Posters">Quality Posters</MenuItem>
                <MenuItem value="Energy Posters">Energy Posters</MenuItem>
                <MenuItem value="Environment Posters">Environment Posters</MenuItem>
                <MenuItem value="Productivity Posters">Productivity Posters</MenuItem>
              </Select>
            </FormControl>

            <TextField
              margin="dense"
              id="images"
              label="Images"
              type="text"
              fullWidth
              value={images}
              onChange={(e) => setImages(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            {editingposterId ? (
              <Button onClick={handleUpdateposter}>Update poster</Button>
            ) : (
              <Button onClick={handleAddposter}>Add poster</Button>
            )}
          </DialogActions>
        </Dialog>

        <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)}>
          <DialogTitle>Poster Details</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              id="title"
              label="Title"
              type="text"
              fullWidth
              value={selectedposter?.title}
              disabled
            />
            <TextField
              margin="dense"
              id="images"
              label="Images"
              type="text"
              fullWidth
              value={selectedposter?.images}
              disabled
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
